// 통합 모달 컴포넌트 - 모든 생성/편집 모달을 하나로 통합
import React, { useEffect } from 'react';
import { Modal, Form, Input, Select, InputNumber, Checkbox, Row, Col, Tabs, Space, Button, Divider, Alert } from 'antd';
import type { TabsProps } from 'antd';
import { 
  SkillFormData, 
  EffectFormData, 
  TriggerFormData, 
  BossFormData,
  WarningDetail,
  BossInfo,
  BossSkill,
  BossEffect,
  TriggerDefinitionV2
} from '../../types';
import { useReferenceData } from '../../contexts/ReferenceDataContext';
import { utilityApi, referenceApi } from '../../services/api';
import { entityApi } from '../../services/entityApi';
import { supabase } from '../../lib/supabase';
import WarningConfigSection from './WarningConfigSection';
import PowerCalculationSection from './PowerCalculationSection';
import EffectDurationTab from '../boss/EffectDurationTab';
import TriggerConditionSection from '../sections/TriggerConditionSection';

const { Option } = Select;
const { TextArea } = Input;

interface UnifiedModalProps {
  visible: boolean;
  onCancel: () => void;
  onOk: (data: BossFormData | SkillFormData | EffectFormData | TriggerFormData) => Promise<void>;
  modalType: 'boss' | 'skill' | 'effect' | 'trigger';
  editingItem?: BossInfo | BossSkill | BossEffect | TriggerDefinitionV2 | null;
  mode: 'create' | 'edit';
  currentBossCode?: string;
}

const UnifiedModal: React.FC<UnifiedModalProps> = ({
  visible,
  onCancel,
  onOk,
  modalType,
  editingItem,
  mode,
  currentBossCode,
}) => {
  const [form] = Form.useForm();
  const { data: referenceData, getSubtypesByParent, getStatsByCategory } = useReferenceData();
  const [loading, setLoading] = React.useState(false);
  const [autoGeneratedKey, setAutoGeneratedKey] = React.useState('');
  const [bossList, setBossList] = React.useState<BossInfo[]>([]);
  const [availableEntities, setAvailableEntities] = React.useState<any[]>([]);
  const [targetTypes, setTargetTypes] = React.useState<any[]>([]);
  const [areaTypes, setAreaTypes] = React.useState<any[]>([]);
  const [selectedAreaType, setSelectedAreaType] = React.useState<string>('');
  const [customAreaDescription, setCustomAreaDescription] = React.useState<string>('');
  
  // 효과 참조 데이터
  const [effectTypes, setEffectTypes] = React.useState<any[]>([]);
  const [effectAffects, setEffectAffects] = React.useState<any[]>([]);
  const [effectDispels, setEffectDispels] = React.useState<any[]>([]);
  const [stats, setStats] = React.useState<any[]>([]);
  const [selectedEffectCat, setSelectedEffectCat] = React.useState<string>('');

  // 모달 제목 설정
  const getModalTitle = () => {
    const titles = {
      boss: mode === 'create' ? '새 보스 생성' : '보스 편집',
      skill: mode === 'create' ? '새 스킬 생성' : '스킬 편집',
      effect: mode === 'create' ? '새 효과 생성' : '효과 편집',
      trigger: mode === 'create' ? '새 발동조건 생성' : '발동조건 편집',
    };
    return titles[modalType];
  };

  // 자동 Key 생성
  const generateAutoKey = async () => {
    if (modalType === 'boss') return;
    
    try {
      const bossCode = form.getFieldValue('boss_code') || currentBossCode || 'COMMON';
      const nameFieldMap = {
        skill: 'name',
        effect: 'name',
        trigger: 'name'
      };
      const nameField = nameFieldMap[modalType as keyof typeof nameFieldMap];
      const name = form.getFieldValue(nameField) || `New${modalType}`;
      
      const key = await utilityApi.generateKey(modalType, bossCode, name);
      setAutoGeneratedKey(key);
      form.setFieldValue(getKeyFieldName(), key);
    } catch (error) {
      console.error('Key 생성 실패:', error);
    }
  };

  // Key 필드명 반환
  const getKeyFieldName = () => {
    const fieldNames = {
      boss: 'boss_code',
      skill: 'key',
      effect: 'key',
      trigger: 'id',
    };
    return fieldNames[modalType];
  };

  // 보스 목록 가져오기
  const loadBossList = async () => {
    try {
      const { bossApi } = await import('../../services/api');
      const bosses = await bossApi.getAll();
      setBossList(bosses);
    } catch (error) {
      console.error('보스 목록 로드 실패:', error);
    }
  };
  
  const loadEffectReferenceData = async () => {
    try {
      const [types, affects, dispels, statData] = await Promise.all([
        referenceApi.fetchEffectTypes(),
        referenceApi.fetchEffectAffects(),
        referenceApi.fetchEffectDispels(),
        referenceApi.fetchStats()
      ]);
      
      setEffectTypes(types);
      setEffectAffects(affects);
      setEffectDispels(dispels);
      setStats(statData);
    } catch (error) {
      console.error('❌ 효과 참조 데이터 로드 실패:', error);
    }
  };

  // 폼 초기화
  useEffect(() => {
    if (visible) {
      // 보스 목록 로드
      if (modalType === 'skill' || modalType === 'effect') {
        loadBossList();
      }
      
      // 스킬 모달인 경우 엔티티와 타겟 타입 로드
      if (modalType === 'skill') {
        loadEntitiesAndTargets();
      }
      
      // 효과 모달인 경우 참조 데이터 로드
      if (modalType === 'effect') {
        loadEffectReferenceData();
      }
      
      if (mode === 'edit' && editingItem) {
        // 편집 모드: 기존 데이터로 폼 초기화
        form.setFieldsValue(editingItem);
        // 스킬 편집시 area가 CUSTOM이면 selectedAreaType 설정
        if (modalType === 'skill' && 'area' in editingItem) {
          setSelectedAreaType(editingItem.area);
        }
        // 효과 편집시 cat 값 확인 (초기화 시점에는 effectTypes가 로드되지 않았을 수 있음)
        if (modalType === 'effect' && 'cat' in editingItem) {
        }
      } else {
        // 생성 모드: 빈 폼 초기화
        form.resetFields();
        // 현재 보스 코드 설정
        if (currentBossCode && modalType !== 'boss') {
          form.setFieldValue('boss_code', currentBossCode);
        }
        generateAutoKey();
      }
    }
  }, [visible, mode, editingItem, form, modalType, currentBossCode]);

  // effectTypes가 로드된 후 편집 모드에서 즉시 효과 판단
  useEffect(() => {
    if (mode === 'edit' && modalType === 'effect' && editingItem && 'cat' in editingItem && effectTypes.length > 0) {
      const effectType = effectTypes.find(type => type.effect_code === editingItem.cat);
      if (effectType) {
        const isInstantEffect = effectType.effect_name.includes('[즉시]');
        setSelectedEffectCat(isInstantEffect ? 'instant' : 'normal');
      }
    }
  }, [effectTypes, mode, modalType, editingItem, form]);

  // 엔티티와 타겟 타입 로드
  const loadEntitiesAndTargets = async () => {
    try {
      // 엔티티 목록 로드
      const entities = await entityApi.getAll();
      setAvailableEntities(entities.filter(e => 
        e.entity_code === currentBossCode || 
        (e.parent_code === currentBossCode && e.entity_type === 'SUMMON')
      ));
      
      // 타겟 타입 로드
      const { data: targetTypesData } = await supabase
        .from('inf_target_type_renewed')
        .select('*')
        .order('target_code');
      setTargetTypes(targetTypesData || []);
      
      // 범위 타입 로드
      const { data: areaTypesData, error: areaError } = await supabase
        .from('inf_skill_area_type')
        .select('*')
        .order('sort_order');
      setAreaTypes(areaTypesData || []);
    } catch (error) {
      console.error('엔티티/타겟/범위 타입 로드 실패:', error);
    }
  };

  // 폼 제출 처리
  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      console.log('📋 폼에서 수집한 values:', values);
      console.log('📋 operator 값:', values.operator);
      console.log('📋 in_stat 값:', values.in_stat);
      console.log('📋 out_stat 값:', values.out_stat);
      await onOk(values);
      form.resetFields();
    } catch (error) {
      console.error('❌ UnifiedModal 폼 제출 오류:', error);
      console.error('❌ 에러 상세:', JSON.stringify(error, null, 2));
    } finally {
      setLoading(false);
    }
  };

  // 보스 폼 렌더링
  const renderBossForm = () => (
    <Form form={form} layout="vertical">
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="보스 코드"
            name="boss_code"
            rules={[{ required: true, message: '보스 코드를 입력하세요' }]}
          >
            <Input placeholder="BOSS_KAMIR" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="보스 이름"
            name="boss_name"
            rules={[{ required: true, message: '보스 이름을 입력하세요' }]}
          >
            <Input placeholder="카미르" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="최대 체력" name="max_hp" initialValue={100000}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="페이즈 수" name="phase_count" initialValue={1}>
            <InputNumber min={1} max={10} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item label="설명" name="description">
        <TextArea rows={3} placeholder="보스 설명을 입력하세요" />
      </Form.Item>
    </Form>
  );

  // 스킬 폼 렌더링
  const renderSkillForm = () => {
    const tabItems: TabsProps['items'] = [
      {
        key: 'basic',
        label: '기본 정보',
        children: (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="스킬 키"
                  name="key"
                  rules={[{ required: true, message: '스킬 키를 입력하세요' }]}
                >
                  <Input 
                    placeholder="자동 생성됨"
                    addonAfter={
                      <Button size="small" onClick={generateAutoKey}>
                        자동생성
                      </Button>
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="스킬 이름"
                  name="name"
                  rules={[{ required: true, message: '스킬 이름을 입력하세요' }]}
                >
                  <Input placeholder="강격" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="소속" name="boss_code">
                  <Select placeholder="공통 또는 보스 선택">
                    <Option value="COMMON">공통 스킬</Option>
                    {bossList.map(boss => (
                      <Option key={boss.boss_code} value={boss.boss_code}>
                        {boss.boss_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="스킬 순서" name="order" initialValue={1}>
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="필요 페이즈" name="required_phase" initialValue={1}>
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item label="설명" name="description">
              <TextArea rows={2} placeholder="스킬의 기능 설명" />
            </Form.Item>
            <Form.Item label="기획자 코멘트" name="comment">
              <TextArea 
                rows={3} 
                placeholder="기획자가 개발자에게 전달할 메모 (예: 타이밍 조정 필요, 데미지 밸런스 확인 등)" 
              />
            </Form.Item>
            
            {/* 경고 설정 통합 */}
            <Divider orientation="left">경고 설정</Divider>
            <WarningConfigSection form={form} />
          </Space>
        ),
      },
      {
        key: 'castingAndTarget',
        label: '시전 및 대상 정보',
        children: (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Divider orientation="left">시전 정보</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="시전 시간(초)" name="casting_time_s" initialValue={0}>
                <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="채널링 시간(초)" name="channeling_time_s" initialValue={0}>
                <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="쿨다운(초)" name="self_cooldown_s" initialValue={0}>
                <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="초기 쿨다운(초)" name="initial_cooldown_s" initialValue={0}>
                <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="차단 가능 여부" name="is_interruptable" initialValue={false}>
                <Select>
                  <Option value={true}>차단 가능</Option>
                  <Option value={false}>차단 불가</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Divider orientation="left">대상 및 범위</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="대상 선택"
                name="target"
                rules={[{ required: true, message: '대상을 선택하세요' }]}
              >
                <Select placeholder="대상 선택">
                  <Select.OptGroup label="레이드 엔티티">
                    {availableEntities.map(entity => (
                      <Option key={entity.entity_code} value={entity.entity_code}>
                        {entity.entity_name} ({entity.entity_type})
                      </Option>
                    ))}
                  </Select.OptGroup>
                  <Select.OptGroup label="특정 대상">
                    {targetTypes.map(target => (
                      <Option key={`TARGET_${target.target_code}`} value={`TARGET_${target.target_code}`}>
                        {target.target_name === '버프보유자' || target.target_name === '디버프보유자' ? '효과보유자' : target.target_name}
                      </Option>
                    ))}
                  </Select.OptGroup>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="범위 타입"
                name="area"
                rules={[{ required: true, message: '범위 타입을 선택하세요' }]}
              >
                <Select 
                  onChange={(value) => setSelectedAreaType(value)}
                  placeholder="범위 타입을 선택하세요"
                  notFoundContent={areaTypes.length === 0 ? "데이터를 불러오는 중..." : "데이터가 없습니다"}
                >
                  {areaTypes.map(area => (
                    <Option key={area.area_code} value={area.area_code}>
                      {area.area_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="사거리" name="range" initialValue={0}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="범위 반지름" name="area_radius" initialValue={0}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="대상 수" name="target_count" initialValue={1}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          
          {selectedAreaType === 'CUSTOM' && (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item 
                  label="Custom 범위 설명" 
                  name="custom_area_description"
                  rules={[{ required: true, message: 'Custom 범위 설명을 입력하세요' }]}
                >
                  <Input placeholder="범위에 대한 상세 설명을 입력하세요" />
                </Form.Item>
              </Col>
            </Row>
          )}
          </Space>
        ),
      },
    ];

    return (
      <Form form={form} layout="vertical">
        <Tabs items={tabItems} />
      </Form>
    );
  };

  // 효과 폼 렌더링
  const renderEffectForm = () => {
    return (
      <Form form={form} layout="vertical">
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="효과 키"
                  name="key"
                  rules={[{ required: true, message: '효과 키를 입력하세요' }]}
                >
                  <Input 
                    placeholder="자동 생성됨"
                    disabled={mode === 'edit'}
                    addonAfter={
                      <Button size="small" disabled={mode === 'edit'} onClick={generateAutoKey}>
                        자동생성
                      </Button>
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="효과 이름"
                  name="name"
                  rules={[{ required: true, message: '효과 이름을 입력하세요' }]}
                >
                  <Input placeholder="기절" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="소속" name="boss_code">
                  <Select placeholder="공통 또는 보스 선택">
                    <Option value="COMMON">공통 효과</Option>
                    {bossList.map(boss => (
                      <Option key={boss.boss_code} value={boss.boss_code}>
                        {boss.boss_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="효과 순서" name="order" initialValue={1}>
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="효과 타입"
                  name="cat"
                  rules={[{ required: true, message: '효과 타입을 선택하세요' }]}
                >
                  <Select 
                    placeholder="효과 타입 선택"
                    onChange={(value) => {
                      // 선택한 효과 타입의 cat 값을 폼에 설정
                      const selectedType = effectTypes.find(type => type.effect_code === value);
                      if (selectedType) {
                        // effect_name에서 즉시 효과 여부 판단
                        const isInstantEffect = selectedType.effect_name.includes('[즉시]');
                        setSelectedEffectCat(isInstantEffect ? 'instant' : 'normal');
                                        }
                    }}
                  >
                    {effectTypes.map(type => (
                      <Option key={type.effect_code} value={type.effect_code}>
                        {type.effect_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="영향 대상"
                  name="affect"
                  rules={[{ required: true, message: '영향 대상을 선택하세요' }]}
                >
                  <Select placeholder="영향 대상 선택">
                    {effectAffects.map(affect => (
                      <Option key={affect.affect_code} value={affect.affect_code}>
                        {affect.affect_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            
            {/* 숨겨진 cat 필드 - effect_type 선택 시 자동 설정 */}
            <Form.Item name="cat" hidden>
              <Input />
            </Form.Item>

            <Form.Item label="설명" name="description">
              <TextArea rows={2} />
            </Form.Item>
          </Space>
          
          <Divider orientation="left">분류 및 속성</Divider>
          
          {/* cat 필드 감시 및 즉시 효과 체크 */}
          <Form.Item noStyle dependencies={['cat']}>
            {({ getFieldValue }) => {
              const catValue = getFieldValue('cat');
              const selectedType = effectTypes.find(type => type.effect_code === catValue);
              const isInstantEffect = selectedType ? selectedType.effect_name.includes('[즉시]') : false;
              
              return (
                <Space direction="vertical" style={{ width: '100%' }}>
                  {isInstantEffect ? (
                    <Alert
                      message="즉시 효과"
                      description="즉시 효과는 분류 및 속성 설정이 필요하지 않습니다."
                      type="info"
                      showIcon
                    />
                  ) : (
                    <>
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item label="해제 방법" name="dispel">
                            <Select placeholder="해제 방법 선택">
                              {effectDispels.map(dispel => (
                                <Option key={dispel.dispel_code} value={dispel.dispel_code}>
                                  {dispel.dispel_name}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item label="최대 중첩" name="max_count" initialValue={1}>
                            <InputNumber min={1} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item label="공유 가능" name="is_shared" valuePropName="checked">
                            <Checkbox>파티원과 공유</Checkbox>
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item label="상호 작용" name="is_mutual" valuePropName="checked">
                            <Checkbox>상호 작용 가능</Checkbox>
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  )}
                </Space>
              );
            }}
          </Form.Item>
          
          <Divider orientation="left">위력 계산</Divider>
          <PowerCalculationSection form={form} stats={stats} effectTypes={effectTypes} />
        </Space>
      </Form>
    );
  };

  // 트리거 폼 렌더링
  const renderTriggerForm = () => (
    <Form form={form} layout="vertical">
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="트리거 ID"
            name="id"
            rules={[{ required: true, message: '트리거 ID를 입력하세요' }]}
          >
            <Input 
              placeholder="자동 생성됨"
              addonAfter={
                <Button size="small" onClick={generateAutoKey}>
                  자동생성
                </Button>
              }
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="트리거 이름"
            name="name"
            rules={[{ required: true, message: '트리거 이름을 입력하세요' }]}
          >
            <Input placeholder="페이즈 2 진입" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label="트리거 타입 (V2 시스템)" name="trigger_type">
            <Select placeholder="V2 트리거는 조건 기반으로 작동합니다">
              <Option value="CONDITION">조건 기반 트리거</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item dependencies={['trigger_type']}>
            {({ getFieldValue }) => (
              <Form.Item 
                label="서브 타입" 
                name="trigger_subtype"
              >
                <Select 
                  placeholder="서브 타입 선택"
                  disabled={!getFieldValue('trigger_type')}
                >
                  {getSubtypesByParent(getFieldValue('trigger_type') || '').map(subtype => (
                    <Option key={subtype.subtype_code} value={subtype.subtype_code}>
                      {subtype.subtype_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="조건값" name="condition_value">
            <Input placeholder="50 (체력 50%), KAMIR_skill_01 (스킬명)" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="반복 가능" name="is_repeatable" valuePropName="checked">
            <Checkbox>반복 발동 허용</Checkbox>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="최대 발동 횟수" name="max_trigger_count">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="0 = 무제한" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item label="설명" name="description">
        <TextArea rows={3} />
      </Form.Item>

      <Divider orientation="left">대사 및 경고 메시지</Divider>
      
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="대사 메시지" name="dialogue_message">
            <TextArea 
              rows={2} 
              placeholder="예: 파멸시켜주마!"
              showCount
              maxLength={100}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="경고 메시지" name="warning_message">
            <TextArea 
              rows={2} 
              placeholder="예: 검투사가 분노를 방출합니다."
              showCount
              maxLength={100}
            />
          </Form.Item>
        </Col>
      </Row>
      
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label="대사 지속시간(초)" name="dialogue_duration">
            <InputNumber 
              min={1} 
              max={10} 
              step={0.5} 
              placeholder="3" 
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="경고 지속시간(초)" name="warning_duration">
            <InputNumber 
              min={1} 
              max={10} 
              step={0.5} 
              placeholder="2" 
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="경고 유형" name="warning_type">
            <Select placeholder="경고 유형 선택">
              <Option value="DANGER">위험 (빨간색)</Option>
              <Option value="WARNING">주의 (노란색)</Option>
              <Option value="INFO">정보 (파란색)</Option>
              <Option value="SCREEN_FLASH">화면 번쩍임</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <TriggerConditionSection 
        form={form} 
        triggerType={form.getFieldValue('trigger_type')}
        triggerSubtype={form.getFieldValue('trigger_subtype')}
      />
      
      {/* Hidden field to store JSON */}
      <Form.Item name="condition_detail" hidden>
        <Input />
      </Form.Item>
    </Form>
  );

  // 폼 타입에 따른 렌더링
  const renderForm = () => {
    switch (modalType) {
      case 'boss': return renderBossForm();
      case 'skill': return renderSkillForm();
      case 'effect': return renderEffectForm();
      case 'trigger': return renderTriggerForm();
      default: return null;
    }
  };

  return (
    <Modal
      title={getModalTitle()}
      open={visible}
      onCancel={() => {
        if (loading) return;
        setSelectedEffectCat('');
        onCancel();
      }}
      onOk={handleSubmit}
      confirmLoading={loading}
      okText="저장"
      cancelText="취소"
      cancelButtonProps={{disabled:loading}}
      closable={!loading}
      maskClosable={!loading}
      width={modalType === 'boss' ? 600 : 800}
      destroyOnClose
    >
      {renderForm()}
    </Modal>
  );
};

export default UnifiedModal;
